import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Save, Droplets, Globe, AlertTriangle, MapPin, Plus, Trash2 } from 'lucide-react';
import MediaField from '../../../components/admin/MediaField';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_WATER_SYSTEM_COST = 75;

/* Section keys — must stay in sync with SECTION_KEYS in backend/server.py.
   Order here also drives the order of checkboxes in the UI. */
const SECTIONS = [
  { key: 'hero',              label: 'Hero banner' },
  { key: 'overview',          label: 'Program overview' },
  { key: 'food_bucket',       label: 'Food Bucket tile' },
  { key: 'hygiene_kit',       label: 'Hygiene Kit tile' },
  { key: 'emergency_support', label: 'Emergency Support (crisis-only)' },
  { key: 'water',             label: 'Clean Water Access' },
  { key: 'locations',         label: 'Locations / Areas within country' },
  { key: 'donation_tiers',    label: 'Donation form + tiers' },
  { key: 'transparency',      label: 'Transparency Notice' },
  { key: 'gallery',           label: 'Program Gallery' },
];

const defaultSections = () => {
  const d = {};
  SECTIONS.forEach((s) => { d[s.key] = s.key !== 'emergency_support'; });
  return d;
};

const uid = () => `loc_${Math.random().toString(36).slice(2, 10)}`;

const ProgramEditor = ({ program, onSave, onCancel }) => {
  const [form, setForm] = useState({
    monthly_goal: program.monthly_goal || 0,
    amount_raised: program.amount_raised || 0,
    families_target: program.families_target || 0,
    families_supported: program.families_supported || 0,
    cost_per_family: program.cost_per_family || 0,
    use_global_content: program.use_global_content !== false,
    hero_copy: program.hero_copy || '',
    overview_copy: program.overview_copy || '',
    transparency_copy: program.transparency_copy || '',
    primary_cta: program.primary_cta || '',
    recurring_cta: program.recurring_cta || '',
    impact_line: program.impact_line || '',
    status: program.status || 'active',
    donations_enabled: program.donations_enabled !== false,
    hero_image: program.hero_image || '',
    // Section visibility toggles
    sections_enabled: { ...defaultSections(), ...(program.sections_enabled || {}) },
    // Food / hygiene descriptions (per-country when global is off)
    food_bucket_description: program.food_bucket_description || '',
    hygiene_kit_description: program.hygiene_kit_description || '',
    food_bucket_cost: program.food_bucket_cost || 0,
    hygiene_bucket_cost: program.hygiene_bucket_cost || 0,
    // Emergency Support
    emergency_support_title: program.emergency_support_title || 'Emergency Response',
    emergency_support_description: program.emergency_support_description || '',
    emergency_support_image: program.emergency_support_image || '',
    emergency_support_cost: program.emergency_support_cost || 0,
    emergency_support_cta: program.emergency_support_cta || 'Support Emergency Response',
    emergency_support_crisis_campaign_id: program.emergency_support_crisis_campaign_id || '',
    // Locations
    locations: (program.locations || []).map((l) => ({
      id: l.id || uid(),
      name: l.name || '',
      description: l.description || '',
      image: l.image || '',
    })),
    // Water
    water_section_visible: program.water_section_visible !== false,
    water_system_cost: program.water_system_cost || DEFAULT_WATER_SYSTEM_COST,
    water_systems_distributed: program.water_systems_distributed || 0,
    water_households_supported: program.water_households_supported || 0,
    water_liters_provided: program.water_liters_provided || 0,
    water_image: program.water_image || '',
    water_description: program.water_description || 'Providing safe drinking water solutions for families through long-lasting filtration systems.',
  });

  // Load active crisis campaigns so admins can link the Emergency
  // Support section to a specific one — donations from that section
  // will be tagged with the campaign id in payment_transactions.
  const [crisisCampaigns, setCrisisCampaigns] = useState([]);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/admin/crisis-campaigns?status=active`,
      { withCredentials: true })
      .then((r) => setCrisisCampaigns(r.data?.campaigns || []))
      .catch(() => setCrisisCampaigns([]));
  }, []);

  const inputClass = "w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm";

  const toggleSection = (key) => setForm((f) => ({
    ...f,
    sections_enabled: { ...f.sections_enabled, [key]: !f.sections_enabled[key] },
  }));

  const updateLocation = (id, patch) => setForm((f) => ({
    ...f,
    locations: f.locations.map((l) => (l.id === id ? { ...l, ...patch } : l)),
  }));

  const addLocation = () => setForm((f) => ({
    ...f,
    locations: [...f.locations, { id: uid(), name: '', description: '', image: '' }],
  }));

  const removeLocation = (id) => setForm((f) => ({
    ...f,
    locations: f.locations.filter((l) => l.id !== id),
  }));

  // Sync water_section_visible with sections_enabled.water so the two
  // legacy toggles stay in lockstep and can't disagree on save.
  const handleSave = () => {
    const payload = {
      ...form,
      water_section_visible: form.sections_enabled.water !== false,
    };
    onSave(payload);
  };

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-6" data-testid="program-editor">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-heading font-bold text-lg">
          Edit: {program.country_name} — {program.program_title}
        </h3>
        <button onClick={onCancel} className="text-[#4B5563] hover:text-[#0A0A0A]" data-testid="program-editor-close">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Global content toggle */}
      <div className="bg-[#FEF9E7] border-2 border-[#F4D03F] rounded-lg p-4 mb-6" data-testid="use-global-content-block">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Globe className="w-5 h-5 text-[#BA1826] mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-heading text-sm font-bold text-[#0A0A0A]">Use Global Program Content</p>
              <p className="text-xs text-[#4B5563] mt-0.5">
                {form.use_global_content
                  ? 'ON — Food Bucket, Hygiene Kit items & Cost/Family will pull from Page Content → Global Country Program Content.'
                  : 'OFF — This country uses its own custom values. Global changes will not affect this program.'}
              </p>
            </div>
          </div>
          <button type="button" onClick={() => setForm({...form, use_global_content: !form.use_global_content})}
            className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${form.use_global_content ? 'bg-[#BA1826]' : 'bg-[#D1D5DB]'}`}
            data-testid="use-global-content-toggle">
            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${form.use_global_content ? 'translate-x-7' : 'translate-x-1'}`} />
          </button>
        </div>
      </div>

      {/* Section visibility — per-country show/hide */}
      <div className="border border-[#E5E7EB] rounded-lg p-4 mb-6 bg-[#F8F9FA]"
           data-testid="sections-visibility-block">
        <p className="font-heading text-sm font-bold text-[#0A0A0A] mb-2">
          Section Visibility
        </p>
        <p className="text-xs text-[#4B5563] mb-3">
          Turn any section on or off for this country. Emergency Support is off by default — flip it on only during a crisis campaign.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {SECTIONS.map((s) => (
            <label key={s.key} className="flex items-center gap-2 text-sm text-[#0A0A0A] cursor-pointer">
              <input
                type="checkbox"
                checked={form.sections_enabled[s.key] !== false}
                onChange={() => toggleSection(s.key)}
                className="accent-[#BA1826]"
                data-testid={`section-toggle-${s.key}`}
              />
              <span>{s.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Numbers */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-xs font-medium text-[#4B5563] mb-1">Monthly Goal ($)</label>
          <input type="number" value={form.monthly_goal}
            onChange={(e) => setForm({...form, monthly_goal: parseFloat(e.target.value) || 0})}
            className={inputClass} data-testid="monthly-goal-input" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#4B5563] mb-1">Amount Raised ($)</label>
          <input type="number" value={form.amount_raised}
            onChange={(e) => setForm({...form, amount_raised: parseFloat(e.target.value) || 0})}
            className={inputClass} data-testid="amount-raised-input" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#4B5563] mb-1">Families Target</label>
          <input type="number" value={form.families_target}
            onChange={(e) => setForm({...form, families_target: parseInt(e.target.value) || 0})}
            className={inputClass} data-testid="families-target-input" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#4B5563] mb-1">
            Cost per Family ($) {form.use_global_content && <span className="text-[10px] text-[#BA1826] font-semibold">(managed globally)</span>}
          </label>
          <input type="number" value={form.cost_per_family}
            onChange={(e) => setForm({...form, cost_per_family: parseFloat(e.target.value) || 0})}
            disabled={form.use_global_content}
            className={`${inputClass} ${form.use_global_content ? 'bg-[#F8F9FA] text-[#94A3B8] cursor-not-allowed' : ''}`}
            data-testid="cost-per-family-input" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#4B5563] mb-1">Status</label>
          <select value={form.status}
            onChange={(e) => setForm({...form, status: e.target.value})}
            className={inputClass} data-testid="status-select">
            <option value="active">Active</option>
            <option value="pending">Pending (Visible but marked as pending)</option>
            <option value="coming_soon">Coming Soon</option>
            <option value="paused">Paused (Hidden)</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#4B5563] mb-1">Donations</label>
          <div className="flex items-center gap-3 h-[38px]">
            <button type="button" onClick={() => setForm({...form, donations_enabled: !form.donations_enabled})}
              className={`relative w-12 h-6 rounded-full transition-colors ${form.donations_enabled ? 'bg-[#22C55E]' : 'bg-[#D1D5DB]'}`}
              data-testid="donations-enabled-toggle">
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${form.donations_enabled ? 'translate-x-7' : 'translate-x-1'}`} />
            </button>
            <span className={`text-xs font-semibold ${form.donations_enabled ? 'text-green-700' : 'text-gray-500'}`}>
              {form.donations_enabled ? 'Donations ON' : 'Donations OFF'}
            </span>
          </div>
        </div>
        <div>
          <MediaField
            label="Hero Image"
            value={form.hero_image}
            onChange={(url) => setForm({ ...form, hero_image: url })}
            accept="image/*"
            testid="hero-image-field"
            hint="Landscape image, ≥1600×900 recommended. Max 10MB. JPG, PNG, WebP, or GIF." />
        </div>
      </div>

      {/* Copy fields */}
      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-xs font-medium text-[#4B5563] mb-1">Hero Copy</label>
          <textarea value={form.hero_copy} onChange={(e) => setForm({...form, hero_copy: e.target.value})}
            className={inputClass} rows={2} data-testid="hero-copy-input" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#4B5563] mb-1">Overview Copy</label>
          <textarea value={form.overview_copy} onChange={(e) => setForm({...form, overview_copy: e.target.value})}
            className={inputClass} rows={3} data-testid="overview-copy-input" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#4B5563] mb-1">Transparency Copy</label>
          <textarea value={form.transparency_copy} onChange={(e) => setForm({...form, transparency_copy: e.target.value})}
            className={inputClass} rows={2} data-testid="transparency-copy-input" />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-[#4B5563] mb-1">Primary CTA</label>
            <input type="text" value={form.primary_cta} onChange={(e) => setForm({...form, primary_cta: e.target.value})}
              className={inputClass} data-testid="primary-cta-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4B5563] mb-1">Recurring CTA</label>
            <input type="text" value={form.recurring_cta} onChange={(e) => setForm({...form, recurring_cta: e.target.value})}
              className={inputClass} data-testid="recurring-cta-input" />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#4B5563] mb-1">Impact Line</label>
          <input type="text" value={form.impact_line} onChange={(e) => setForm({...form, impact_line: e.target.value})}
            className={inputClass} data-testid="impact-line-input" />
        </div>
      </div>

      {/* Food + Hygiene descriptions & costs */}
      <div className="border-t border-[#E5E7EB] pt-5 mb-6">
        <p className="font-heading text-sm font-bold text-[#0A0A0A] mb-3">
          Food &amp; Hygiene Descriptions
        </p>
        <p className="text-xs text-[#4B5563] mb-3">
          {form.use_global_content
            ? 'Using Global Content — costs/items come from Page Content. Descriptions below still override the default caption on this country page.'
            : 'This country uses its own custom values below.'}
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-[#4B5563] mb-1">
              Food Bucket Description
            </label>
            <textarea value={form.food_bucket_description}
              onChange={(e) => setForm({...form, food_bucket_description: e.target.value})}
              className={inputClass} rows={2}
              placeholder="e.g. Rice, beans, oil, and pantry staples for one month."
              data-testid="food-bucket-desc-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4B5563] mb-1">
              Food Bucket Cost ($) {form.use_global_content && <span className="text-[10px] text-[#BA1826] font-semibold">(global)</span>}
            </label>
            <input type="number" value={form.food_bucket_cost}
              onChange={(e) => setForm({...form, food_bucket_cost: parseFloat(e.target.value) || 0})}
              disabled={form.use_global_content}
              className={`${inputClass} ${form.use_global_content ? 'bg-[#F8F9FA] text-[#94A3B8] cursor-not-allowed' : ''}`}
              data-testid="food-bucket-cost-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4B5563] mb-1">
              Hygiene Kit Description
            </label>
            <textarea value={form.hygiene_kit_description}
              onChange={(e) => setForm({...form, hygiene_kit_description: e.target.value})}
              className={inputClass} rows={2}
              placeholder="e.g. Soap, toothbrush, sanitary pads — a month of essentials."
              data-testid="hygiene-kit-desc-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4B5563] mb-1">
              Hygiene Kit Cost ($) {form.use_global_content && <span className="text-[10px] text-[#BA1826] font-semibold">(global)</span>}
            </label>
            <input type="number" value={form.hygiene_bucket_cost}
              onChange={(e) => setForm({...form, hygiene_bucket_cost: parseFloat(e.target.value) || 0})}
              disabled={form.use_global_content}
              className={`${inputClass} ${form.use_global_content ? 'bg-[#F8F9FA] text-[#94A3B8] cursor-not-allowed' : ''}`}
              data-testid="hygiene-kit-cost-input" />
          </div>
        </div>
      </div>

      {/* Emergency Support Section */}
      <div className="border-t border-[#E5E7EB] pt-5 mb-6"
           data-testid="emergency-support-block">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-heading font-bold text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-[#B91C1C]" /> Emergency Support
          </h4>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox"
              checked={form.sections_enabled.emergency_support === true}
              onChange={() => toggleSection('emergency_support')}
              className="accent-[#BA1826]"
              data-testid="emergency-support-visible-checkbox" />
            Show on country page
          </label>
        </div>
        <p className="text-xs text-[#4B5563] mb-3">
          Turn this on during a crisis (earthquake, hurricane, etc.). Donations made from this section are tagged <code>is_emergency=true</code> in the ledger for separate reporting.
        </p>
        <div className="grid md:grid-cols-2 gap-4 mb-3">
          <div>
            <label className="block text-xs font-medium text-[#4B5563] mb-1">Section Title</label>
            <input type="text" value={form.emergency_support_title}
              onChange={(e) => setForm({...form, emergency_support_title: e.target.value})}
              className={inputClass}
              placeholder="e.g. Chocó Earthquake Response"
              data-testid="emergency-title-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4B5563] mb-1">Suggested Donation ($)</label>
            <input type="number" value={form.emergency_support_cost} min="0" step="0.01"
              onChange={(e) => setForm({...form, emergency_support_cost: parseFloat(e.target.value) || 0})}
              className={inputClass}
              data-testid="emergency-cost-input" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-[#4B5563] mb-1">Description</label>
            <textarea value={form.emergency_support_description}
              onChange={(e) => setForm({...form, emergency_support_description: e.target.value})}
              className={inputClass} rows={3}
              placeholder="What happened, who's affected, and how this donation helps."
              data-testid="emergency-desc-input" />
          </div>
          <div className="md:col-span-2">
            <MediaField
              label="Emergency Media (image or short video)"
              value={form.emergency_support_image}
              onChange={(url) => setForm({ ...form, emergency_support_image: url })}
              accept="image/*,video/*"
              testid="emergency-media-field"
              hint="Field photo or a short clip. Images ≤10MB, videos ≤100MB (mp4/webm/mov)." />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4B5563] mb-1">Donate Button Text</label>
            <input type="text" value={form.emergency_support_cta}
              onChange={(e) => setForm({...form, emergency_support_cta: e.target.value})}
              className={inputClass}
              data-testid="emergency-cta-input" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-[#4B5563] mb-1">
              Link to Crisis Campaign (optional — tags donations for the Crisis Response Ledger)
            </label>
            <select value={form.emergency_support_crisis_campaign_id}
              onChange={(e) => setForm({...form, emergency_support_crisis_campaign_id: e.target.value})}
              className={inputClass}
              data-testid="emergency-crisis-campaign-select">
              <option value="">— None (donations not tagged to a campaign) —</option>
              {crisisCampaigns.map((cc) => (
                <option key={cc.id} value={cc.id}>{cc.name}</option>
              ))}
            </select>
            <p className="text-[10px] text-[#9CA3AF] mt-1">
              Create campaigns in <strong>Crisis Response</strong> tab. Only active campaigns are listed.
            </p>
          </div>
        </div>
      </div>

      {/* Locations / Areas within country */}
      <div className="border-t border-[#E5E7EB] pt-5 mb-6"
           data-testid="locations-block">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-heading font-bold text-sm flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#BA1826]" /> Locations / Areas
          </h4>
          <button type="button" onClick={addLocation}
            className="text-xs flex items-center gap-1 bg-[#0A0A0A] text-white px-3 py-1.5 rounded hover:bg-gray-800"
            data-testid="add-location-btn">
            <Plus className="w-3 h-3" /> Add location
          </button>
        </div>
        <p className="text-xs text-[#4B5563] mb-3">
          Sub-areas inside this country (e.g. Yumbo, Chocó, Risaralda). Toggle the Locations section on to display them on the country page.
        </p>
        {form.locations.length === 0 ? (
          <p className="text-xs text-[#9CA3AF] italic bg-[#F8F9FA] border border-dashed border-[#E5E7EB] rounded p-4 text-center">
            No locations yet — click <strong>Add location</strong> to include an area within this country.
          </p>
        ) : (
          <div className="space-y-3">
            {form.locations.map((loc, idx) => (
              <div key={loc.id}
                   className="border border-[#E5E7EB] rounded p-3 bg-white"
                   data-testid={`location-row-${idx}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-[#4B5563]">Location {idx + 1}</span>
                  <button type="button" onClick={() => removeLocation(loc.id)}
                    className="text-[#B91C1C] hover:text-red-700"
                    data-testid={`remove-location-${idx}`}
                    aria-label="Remove location">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <input type="text" value={loc.name}
                    onChange={(e) => updateLocation(loc.id, { name: e.target.value })}
                    className={inputClass}
                    placeholder="Location name (e.g. Yumbo)"
                    data-testid={`location-name-${idx}`} />
                  <textarea value={loc.description}
                    onChange={(e) => updateLocation(loc.id, { description: e.target.value })}
                    className={inputClass} rows={2}
                    placeholder="Short description of the work in this area."
                    data-testid={`location-desc-${idx}`} />
                  <div className="md:col-span-2">
                    <MediaField
                      label="Location Image"
                      value={loc.image}
                      onChange={(url) => updateLocation(loc.id, { image: url })}
                      accept="image/*"
                      testid={`location-media-${idx}`}
                      aspectClass="aspect-[16/9]" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Clean Water Access Section */}
      <div className="border-t border-[#E5E7EB] pt-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-heading font-bold text-sm flex items-center gap-2">
            <Droplets className="w-4 h-4 text-[#1E6B8A]" /> Clean Water Access
          </h4>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox"
              checked={form.sections_enabled.water !== false}
              onChange={() => toggleSection('water')}
              className="accent-[#BA1826]"
              data-testid="water-visible-checkbox" />
            Visible on page
          </label>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-[#4B5563] mb-1">Cost per Water System ($)</label>
            <input type="number" value={form.water_system_cost} min="0" step="0.01"
              onChange={(e) => setForm({...form, water_system_cost: parseFloat(e.target.value) || 0})}
              className={inputClass} data-testid="water-cost-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4B5563] mb-1">Systems Distributed</label>
            <input type="number" value={form.water_systems_distributed} min="0"
              onChange={(e) => setForm({...form, water_systems_distributed: parseInt(e.target.value) || 0})}
              className={inputClass} data-testid="water-systems-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4B5563] mb-1">Households Supported</label>
            <input type="number" value={form.water_households_supported} min="0"
              onChange={(e) => setForm({...form, water_households_supported: parseInt(e.target.value) || 0})}
              className={inputClass} data-testid="water-households-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4B5563] mb-1">Liters Provided</label>
            <input type="number" value={form.water_liters_provided} min="0"
              onChange={(e) => setForm({...form, water_liters_provided: parseInt(e.target.value) || 0})}
              className={inputClass} data-testid="water-liters-input" />
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-[#4B5563] mb-1">Water Section Description</label>
            <textarea value={form.water_description} onChange={(e) => setForm({...form, water_description: e.target.value})}
              className={inputClass} rows={2} />
          </div>
          <MediaField
            label="Water Section Image"
            value={form.water_image}
            onChange={(url) => setForm({ ...form, water_image: url })}
            accept="image/*"
            testid="water-image-field" />
        </div>
      </div>

      <div className="flex gap-2 sticky bottom-0 bg-white pt-3 border-t border-[#E5E7EB]">
        <button onClick={handleSave} className="btn-primary" data-testid="save-program-btn">
          <Save className="w-4 h-4" /> Save Changes
        </button>
        <button onClick={onCancel} className="btn-secondary">Cancel</button>
      </div>
    </div>
  );
};

export default ProgramEditor;
